/**
 * Survey order — the one sequence a crosstab's rows are ordered by (M4S-5595 item 6).
 *
 * The team's decision, 2026-09-21: rows appear in the order the survey was programmed in, not in
 * the order a template prescribes. `sortOrder` alone cannot express that for a project holding more
 * than one survey — it is `survey_question.question_sort`, which restarts at 1 in every wave, so
 * two waves' first questions both hold 1 and the list interleaves. This module resolves that once,
 * on the server, and stamps each question with a dense position the client can simply sort by.
 *
 * **The rule, per Vardan, 2026-09-21.** Across waves the questions should be the same, and in a
 * well-formed multi-wave study they are: they collapse to one entry in `dedupeQuestions` and their
 * order is unambiguous. Only questions that are NOT in every survey need deciding, and those are
 * grouped by the survey they came from so a user can see where they came from rather than finding
 * them interleaved with the common ones.
 *
 * So:
 *
 * 1. Questions present in **every** survey come first, in `sortOrder`.
 * 2. Then, survey by survey in the project's own survey order, the questions that survey has and
 *    the shared block does not — each group in `sortOrder`.
 *
 * A question in two of three surveys belongs to the earlier of the two. A single-survey project has
 * no second group and the rule degenerates to a plain sort, which is the common case.
 *
 * Coded open-ends are not passed through here. They are appended by the project route after this
 * runs and keep their existing place at the end of the list — see `stampTrailingOrder`. Whether
 * they should instead take their programmed position is an open question nobody has answered.
 *
 * **Why the client half lives here too.** `activeRows` is the only ordered thing — `computeFromApi`
 * builds its blocks from it verbatim, so the array IS the table order — but `setActiveRows` is
 * called from twenty-one places and none of them owned the rule. Package B hit the same shape with
 * variable placement and answered it with `cv-placement.ts`: one pure module, every reader routed
 * through it. This is that, for order, and it is in `lib` rather than the component directory
 * because the three template routes need the same sort the workspace does.
 *
 * **What this module does NOT do.** It never re-sorts a list that already exists. A saved layout
 * reopens in the order it was saved, and a drag reorder survives, because ordering is applied where
 * rows *enter* the list — not on load and not on render. "Always survey order" is the default, not
 * an override of what a user arranged by hand; confirmed with Jonathan, 2026-09-21.
 */

import type { CrosstabQuestionDto } from '@/lib/crosstab/question-formatter';

/** `sortOrder` arrives from `pg` as a string (`'3'`, `'11'`). Coerce before comparing. */
const sortKey = (q: CrosstabQuestionDto): number => Number(q.sortOrder) || 0;

const bySortOrder = (a: CrosstabQuestionDto, b: CrosstabQuestionDto) => sortKey(a) - sortKey(b);

/**
 * The index of the earliest survey this question appears in, or `surveyIds.length` when it names
 * none we recognise — those sort after the per-survey groups rather than silently joining one.
 */
function earliestSurveyIndex(q: CrosstabQuestionDto, surveyIds: string[]): number {
    const own = new Set((q.surveyIds ?? []).map(String));
    for (let i = 0; i < surveyIds.length; i++) {
        if (own.has(String(surveyIds[i]))) return i;
    }
    return surveyIds.length;
}

/**
 * Orders `questions` by survey order and stamps each with its `surveyOrder` position.
 *
 * Returns a new array; the inputs are not mutated. `surveyIds` is the project's survey list in the
 * order the project resolves them — the route already sorts it numerically ascending, which is
 * wave order.
 */
export function assignSurveyOrder(
    questions: CrosstabQuestionDto[],
    surveyIds: string[],
): CrosstabQuestionDto[] {
    // One survey (or none) means there is nothing to group: every question is shared by definition.
    if (surveyIds.length <= 1) {
        return [...questions].sort(bySortOrder).map((q, i) => ({ ...q, surveyOrder: i }));
    }

    const shared: CrosstabQuestionDto[] = [];
    // Index i holds the questions whose earliest survey is surveyIds[i]; the last bucket holds
    // questions that name no survey we know about.
    const perSurvey: CrosstabQuestionDto[][] = Array.from(
        { length: surveyIds.length + 1 },
        () => [],
    );

    for (const q of questions) {
        const own = q.surveyIds ?? [];
        // A question the mapper could not attribute is treated as shared rather than exiled: it is
        // missing data about the question, not evidence that the question is wave-specific.
        if (own.length === 0 || own.length >= surveyIds.length) {
            shared.push(q);
            continue;
        }
        perSurvey[earliestSurveyIndex(q, surveyIds)].push(q);
    }

    const ordered = [
        ...shared.sort(bySortOrder),
        ...perSurvey.flatMap((group) => group.sort(bySortOrder)),
    ];

    return ordered.map((q, i) => ({ ...q, surveyOrder: i }));
}

/**
 * Stamps `questions` with positions that continue after `startAt`, keeping their given order.
 *
 * Used for coded open-ends, which the project route concatenates after the survey questions. They
 * carry a synthetic `sortOrder` of 9000+ rather than a real programmed position, so sorting them
 * with the others would be sorting on a number that means nothing.
 */
export function stampTrailingOrder(
    questions: CrosstabQuestionDto[],
    startAt: number,
): CrosstabQuestionDto[] {
    return questions.map((q, i) => ({ ...q, surveyOrder: startAt + i }));
}

/** The shape this module needs from a question. Both the API DTO and the rail catalog satisfy it. */
export interface OrderableQuestion {
    id?: string;
    qid?: string;
    /** Dense position in the project's survey order, assigned server-side by `assignSurveyOrder`. */
    surveyOrder?: number;
    /** Per-survey programmed position. Arrives from `pg` as a string. Fallback only. */
    sortOrder?: number | string;
}

/** A custom variable, for the purpose of placing it next to the question it derives from. */
export interface OrderableVariable {
    id: string;
    sourceQuestionId?: string;
}

/** Sorts after everything known, rather than to the front, when a position cannot be resolved. */
const UNPLACED = Number.MAX_SAFE_INTEGER;

const keyOf = (id: string) => id.toUpperCase();

/**
 * Position lookup for every id that can appear in `activeRows`.
 *
 * A question takes its own `surveyOrder` — or `sortOrder` when the payload predates that field, so
 * a stale response degrades to today's behaviour rather than collapsing every row to one position.
 * A custom variable takes its source question's position plus a half step, which puts it directly
 * after that question without displacing anything else.
 */
export function buildPositionIndex(
    catalog: readonly OrderableQuestion[],
    variables: readonly OrderableVariable[] = [],
): Map<string, number> {
    const pos = new Map<string, number>();

    for (const q of catalog) {
        const id = q.qid ?? q.id;
        if (!id) continue;
        // `surveyOrder` is 0-based and dense, so `??` — not `||` — is required: position 0 is real.
        // `Number(undefined)` is NaN rather than undefined, so the finite check is what catches a
        // question carrying neither field.
        const at = q.surveyOrder ?? Number(q.sortOrder);
        pos.set(keyOf(id), Number.isFinite(at) ? at : UNPLACED);
    }

    for (const cv of variables) {
        const src = cv.sourceQuestionId ? pos.get(keyOf(cv.sourceQuestionId)) : undefined;
        pos.set(keyOf(cv.id), src === undefined ? UNPLACED : src + 0.5);
    }

    return pos;
}

/**
 * A question catalog in survey order.
 *
 * The workspace sorted on `sortOrder` in four places, which is the per-survey key and collides
 * across waves. This is the same sort those four now share.
 */
export function sortQuestionsBySurveyOrder<T extends OrderableQuestion>(
    questions: readonly T[],
): T[] {
    return [...questions].sort((a, b) => {
        const pa = a.surveyOrder ?? Number(a.sortOrder);
        const pb = b.surveyOrder ?? Number(b.sortOrder);
        return (Number.isFinite(pa) ? pa : UNPLACED) - (Number.isFinite(pb) ? pb : UNPLACED);
    });
}

/**
 * `ids` in survey order.
 *
 * Stable: ids that resolve to the same position — several variables on one question, or several
 * ids that resolve to none — keep the order they were given in.
 */
export function sortBySurveyOrder(
    ids: readonly string[],
    catalog: readonly OrderableQuestion[],
    variables: readonly OrderableVariable[] = [],
): string[] {
    const pos = buildPositionIndex(catalog, variables);
    return [...ids].sort(
        (a, b) => (pos.get(keyOf(a)) ?? UNPLACED) - (pos.get(keyOf(b)) ?? UNPLACED),
    );
}

/** True when `list` is already in survey order — i.e. nobody has rearranged it by hand. */
function isInSurveyOrder(list: readonly string[], pos: Map<string, number>): boolean {
    let prev = -Infinity;
    for (const id of list) {
        const at = pos.get(keyOf(id)) ?? UNPLACED;
        if (at < prev) return false;
        prev = at;
    }
    return true;
}

/**
 * `list` with `id` added at its survey position — **when the list is still in survey order**.
 *
 * On a list in survey order this inserts before the first entry that sits later, which is what
 * turns "Load all to rows" from two ascending runs into one.
 *
 * **On a list the user has rearranged, it appends instead.** Once someone has dragged rows into
 * their own order, "its survey position" no longer names a place: scanning for the first later
 * entry lands somewhere the user cannot predict from anything on screen. Appending is the
 * predictable answer, and it keeps the promise that survey order is the default rather than
 * something that reasserts itself over a hand-arranged list. See docs/pdlc/
 * crosstabs-trusted-tool.md §6 package F.
 *
 * An id already present is returned untouched, and an id with no resolvable position goes last.
 */
export function insertAtSurveyPosition(
    list: readonly string[],
    id: string,
    catalog: readonly OrderableQuestion[],
    variables: readonly OrderableVariable[] = [],
): string[] {
    if (list.some((x) => keyOf(x) === keyOf(id))) return [...list];

    const pos = buildPositionIndex(catalog, variables);
    const at = pos.get(keyOf(id)) ?? UNPLACED;
    if (at === UNPLACED || !isInSurveyOrder(list, pos)) return [...list, id];

    const before = list.findIndex((x) => (pos.get(keyOf(x)) ?? UNPLACED) > at);
    if (before === -1) return [...list, id];
    return [...list.slice(0, before), id, ...list.slice(before)];
}

/**
 * `list` with every id in `ids` inserted at its survey position.
 *
 * Applied left to right, so a batch add lands in survey order among itself as well as against what
 * was already there.
 */
export function insertAllAtSurveyPosition(
    list: readonly string[],
    ids: readonly string[],
    catalog: readonly OrderableQuestion[],
    variables: readonly OrderableVariable[] = [],
): string[] {
    return sortBySurveyOrder(ids, catalog, variables).reduce<string[]>(
        (acc, id) => insertAtSurveyPosition(acc, id, catalog, variables),
        [...list],
    );
}

/**
 * M4S-5777 (X29): `list` (the banner) with the ids in `ids` appended in survey order, whatever
 * order they were picked in. Ids already in `list` are skipped. What is already there, and any
 * order someone dragged it into, is left exactly as it is: only the new batch is ordered.
 */
export function appendAllInSurveyOrder(
    list: readonly string[],
    ids: readonly string[],
    catalog: readonly OrderableQuestion[],
    variables: readonly OrderableVariable[] = [],
): string[] {
    const present = new Set(list.map(keyOf));
    const out = [...list];
    for (const id of sortBySurveyOrder(ids, catalog, variables)) {
        if (present.has(keyOf(id))) continue;
        present.add(keyOf(id));
        out.push(id);
    }
    return out;
}
